<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import EditEventOverlayPanel from './components/EditEventOverlayPanel.vue';
import UserProfileHeader from './components/UserProfileHeader.vue';
import Impersonate from './components/Impersonate.vue';
import ImpersonateTenant from './components/ImpersonateTenant.vue';
import UniversalScreenersModal from './components/UniversalScreenersModal.vue';
import { useBeacon } from '@/composables/HelpScoutWidget';
import { can, usePersonTypes, useTenant } from '@/composables';
import { useAuthStore, useMessageCenterStore } from '@/store';
import { PERMISSIONS, TENANT_SETTINGS } from '@/enum';

const useAuth = useAuthStore();
const messageCenter = useMessageCenterStore();
const $tenant = useTenant();
const { screen } = storeToRefs(messageCenter);
const isSupportAdmin = computed<boolean>(() => usePersonTypes.isSupportAdmin());
const isSuperAdmin = computed<boolean>(() => usePersonTypes.isSuperAdmin());
const onAdminPage = ref(false);

onMounted(() => {
  useBeacon(useAuth.user);
  onAdminPage.value = window.location.pathname.includes('/admin');
});

const canAccessBackpack = computed(() => {
  if (usePersonTypes.isClinician()) return true;

  return (useAuth.isSwitchTenant && usePersonTypes.isSupportAdmin()) || usePersonTypes.isSuperAdmin();
});

const openMessageCenter = () => {
  screen.value = 'list-chats';
  messageCenter.visible = true;
};

const openBeacon = () => window.Beacon('toggle');

const backpackRedirect = async () => {
  await useAuth.setTmsBackpackCookie();
  window.location.href = process.env.VUE_APP_BACKPACK_URL as string;
};

const isMessageCenterEnabled = () => {
  return (
    $tenant.tenantSettingEnabled(TENANT_SETTINGS.MESSAGE_CENTER) &&
    !useAuth.isImpersonating &&
    can([PERMISSIONS.CHAT_INDEX])
  );
};

const showScreenersModal = ref(false);

const hasSSISColabUserId = computed(() => {
  return !!useAuth.user?.ssis_colab_user_id;
});

const canAccessUniversalScreeners = computed(() => {
  return usePersonTypes.isSchoolAdmin() && hasSSISColabUserId.value;
});

const openScreenersModal = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
  showScreenersModal.value = true;
};
</script>

<template>
  <nav>
    <ImpersonateTenant />
    <div id="nav">
      <RouterLink v-if="!isSupportAdmin && !onAdminPage" to="/">
        <div id="logo">
          <img src="@/assets/images/burst-white.svg" class="logo-image" />
        </div>
      </RouterLink>
      <div v-else id="logo">
        <img src="@/assets/images/burst-white.svg" class="logo-image" />
      </div>
      <div v-if="onAdminPage && isSupportAdmin">
        <RouterLink to="/admin">
          <span>Insight Admin Panel</span>
        </RouterLink>
      </div>
      <div v-else>
        <RouterLink to="/" type="button" class="mr-3">
          <span>eLuma Insight</span>
        </RouterLink>
        <RouterLink v-if="canAccessBackpack" to="/" class="backpack-link mr-3">
          <span @click="backpackRedirect()">Backpack</span>
        </RouterLink>
        <RouterLink v-if="canAccessUniversalScreeners" to="/" class="universal-screeners-link mr-3">
          <span @click="openScreenersModal">Universal Screeners</span>
        </RouterLink>
      </div>
      <div class="pull-right">
        <div v-if="isMessageCenterEnabled()" class="flex align-items-center relative">
          <Icon name="chat_bubble" class="text-color-secondary mr-3 cursor-pointer" @click="openMessageCenter" />
          <Icon
            v-if="messageCenter.hasUnreadChats"
            name="circle"
            class="absolute top-0 mx-2 -my-1 text-sm cursor-pointer unread-message-indicator"
            @click="openMessageCenter"
          />
        </div>
        <EditEventOverlayPanel v-if="useAuth.loggedIn && isSuperAdmin" />
        <Icon
          v-if="useAuth.loggedIn"
          name="help"
          class="text-color-secondary mr-3 cursor-pointer"
          @click="openBeacon"
        />
        <UserProfileHeader />
        <Impersonate />
      </div>
    </div>
    <UniversalScreenersModal v-model:visible="showScreenersModal" />
  </nav>
</template>

<style lang="scss" scoped>
#nav {
  height: 48px;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: center;

  #logo {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0.5rem;
    width: 2rem;
    height: 2rem;
    background: linear-gradient(to top right, var(--primary-color-medium), var(--primary-color-dark));
    border-radius: 1rem;

    .logo-image {
      width: 1.5rem;
      height: 1.5rem;
    }

    span {
      color: var(--primary-color-dark);
    }
  }

  a {
    font-weight: bold;
    color: var(--primary-color-dark);
    text-decoration: none;
    cursor: pointer;

    &.router-link-exact-active {
      color: var(--primary-color-dark);
    }

    &.backpack-link,
    &.universal-screeners-link {
      color: var(--text-color-secondary);
    }

    &:hover {
      color: var(--primary-color-dark);
    }
  }

  .pull-right {
    margin-left: auto;
    align-items: center;
    display: flex;
    flex-direction: row;
    margin-right: 8px;
  }

  @media (max-width: 480px) {
    padding-left: 40px;
  }

  .message {
    font-size: 18px;
  }
  .unread-message-indicator {
    color: var(--primary-color-medium) !important;
  }
}

nav {
  width: 100%;
  position: fixed;
  top: 0px;
  left: 0px;
  background: rgba(230, 232, 232, 0.5);
  backdrop-filter: blur(12px);
  z-index: 2;
}
</style>
